import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { logoutAsync } from '../../store/authSlice';
import disclaimerService, {
  CurrentDisclaimerResponse,
  onDisclaimerRequired,
} from '../../services/disclaimerService';
import { LogoutCoordinator } from '../../services/logoutCoordinator';
import { setBootOverlayBlocked } from '../../services/appBootState';
import { ConfidentialityDisclaimerModal } from './ConfidentialityDisclaimerModal';
import { LoadingSpinner } from '../common/LoadingSpinner';

/**
 * Session-wide cache of (user, hospital, version) combinations already confirmed.
 *
 * This is MODULE-LEVEL on purpose, not a per-component ref: every ProtectedRoute
 * mounts its own DisclaimerGate, so a per-instance cache would re-fetch
 * /auth/disclaimer/current/ and re-gate the UI on every single navigation. Keys
 * include the userId so one user's acknowledgment can never satisfy another
 * user's gate. The cache is cleared on logout (see the effect below).
 */
const confirmedDisclaimers = new Set<string>();
const cacheKeyFor = (userId: number | null, hospitalId: number | null, version: number | null) =>
  `${userId ?? 'u'}:${hospitalId ?? 'h'}:${version ?? 0}`;
const cachePrefixFor = (userId: number | null, hospitalId: number | null) =>
  `${userId ?? 'u'}:${hospitalId ?? 'h'}:`;

type GateState = 'idle' | 'checking' | 'required' | 'ok';

/**
 * Blocks rendering of authenticated content until the user has acknowledged
 * the current hospital's confidentiality disclaimer.
 *
 * Runs on every hospital change (including initial login and hospital switch)
 * and surfaces the modal if the server indicates acceptance is required.
 */
export const DisclaimerGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const hospital = useSelector((s: RootState) => s.auth.hospital);
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const userId = useSelector((s: RootState) => s.auth.user?.id ?? null);

  const [state, setState] = useState<GateState>('idle');
  const [disclaimer, setDisclaimer] = useState<CurrentDisclaimerResponse | null>(null);

  const hospitalId = hospital?.id ?? null;

  // Latest state, readable from inside async callbacks / event listeners.
  const stateRef = useRef<GateState>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // The boot loading screen must not cover this modal — the user has to answer
  // it before anything else can happen. Tell the overlay to step aside while
  // it's up, and release it again once accepted (or on unmount).
  useEffect(() => {
    setBootOverlayBlocked('disclaimer', state === 'required');
    return () => setBootOverlayBlocked('disclaimer', false);
  }, [state]);

  // Guards against firing multiple concurrent /disclaimer/current/ calls when a
  // burst of 412s arrives (each 412 signals "disclaimer required"). Without this
  // the checks fan out and the modal flickers as each one resolves.
  const inFlightRef = useRef(false);

  const runCheck = useCallback(async () => {
    if (!isAuthenticated || hospitalId === null) {
      setState('ok');
      return;
    }
    // If a logout is already in flight, do not fire /auth/disclaimer/current/.
    // Otherwise we contribute to the 401 storm that triggered the logout in
    // the first place — and once the redirect to /login completes, this
    // component unmounts anyway.
    if (LogoutCoordinator.active) {
      setState('ok');
      return;
    }
    // Coalesce: a burst of 412s must collapse into a single check, and the
    // check must not restart while one is already running.
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    // Only drop to the blocking spinner if the modal is NOT already up.
    // Re-checking while the modal is showing must keep the modal on screen —
    // flipping to a spinner and back is exactly the flashing we're fixing.
    setState((prev) => (prev === 'required' ? prev : 'checking'));
    try {
      const resp = await disclaimerService.fetchCurrent();
      const key = cacheKeyFor(userId, resp.hospital_id ?? hospitalId, resp.version);
      if (!resp.enabled || !resp.acceptance_required) {
        confirmedDisclaimers.add(key);
        setDisclaimer(null);
        setState('ok');
      } else {
        setDisclaimer(resp);
        setState('required');
      }
    } catch (err) {
      // Network / auth issues — fail open (don't block the user over a transient
      // error). But never tear down a modal that's already required over a stray
      // error; if the session is truly broken other guards redirect to login.
      console.warn('[DisclaimerGate] Failed to check disclaimer status:', err);
      setState((prev) => (prev === 'required' ? prev : 'ok'));
    } finally {
      inFlightRef.current = false;
    }
  }, [isAuthenticated, hospitalId, userId]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear the session cache so the next user re-checks from scratch.
      confirmedDisclaimers.clear();
      setState('ok');
      setDisclaimer(null);
      return;
    }
    if (hospitalId === null) {
      setState('ok');
      return;
    }
    // Skip if we've already confirmed this (user, hospital) in-session.
    const prefix = cachePrefixFor(userId, hospitalId);
    const anyConfirmed = Array.from(confirmedDisclaimers).some((k) => k.startsWith(prefix));
    if (anyConfirmed) {
      setState('ok');
      return;
    }
    runCheck();
  }, [isAuthenticated, hospitalId, userId, runCheck]);

  // Listen for "disclaimer required" signals raised by the axios interceptor
  // when any authenticated request returns 412 disclaimer_required. The backend
  // has authoritatively told us this user is not current — invalidate the
  // in-session cache for this hospital and re-run the check.
  useEffect(() => {
    return onDisclaimerRequired((payload) => {
      // The modal is already up (or a check is in flight) — nothing to do.
      // App-level pollers (notifications, chatbot history) keep 412-ing until
      // the user accepts; without this guard each one would re-trigger the
      // gate and make the modal flash.
      if (stateRef.current === 'required' || inFlightRef.current) {
        return;
      }
      const targetHospitalId = payload.hospital_id ?? hospitalId;
      if (targetHospitalId !== null && targetHospitalId !== undefined) {
        const prefix = cachePrefixFor(userId, targetHospitalId);
        Array.from(confirmedDisclaimers)
          .filter((k) => k.startsWith(prefix))
          .forEach((k) => confirmedDisclaimers.delete(k));
      }
      runCheck();
    });
  }, [hospitalId, userId, runCheck]);

  const handleAccept = async () => {
    await disclaimerService.accept();
    if (disclaimer) {
      confirmedDisclaimers.add(cacheKeyFor(userId, disclaimer.hospital_id ?? hospitalId, disclaimer.version));
    }
    setDisclaimer(null);
    setState('ok');
  };

  const handleDecline = async () => {
    try {
      await dispatch(logoutAsync()).unwrap();
    } catch {
      // Even if the logout API call fails we still tear down local auth via the fulfilled/rejected handlers.
    }
  };

  // Block authenticated content until the disclaimer status is resolved, so
  // child pages don't fire a burst of API calls that all 412 (which then
  // re-trigger this gate). Skip the block for an already-confirmed (user,
  // hospital) so navigation between routes never flashes a spinner.
  const needsGate = isAuthenticated && hospitalId !== null && !LogoutCoordinator.active;
  const preConfirmed =
    hospitalId !== null &&
    Array.from(confirmedDisclaimers).some((k) => k.startsWith(cachePrefixFor(userId, hospitalId)));

  if ((state === 'idle' || state === 'checking') && needsGate && !preConfirmed) {
    return <LoadingSpinner size="lg" className="h-screen" />;
  }

  if (state === 'required' && disclaimer) {
    return (
      <ConfidentialityDisclaimerModal
        isOpen
        text={disclaimer.text}
        version={disclaimer.version}
        hospitalName={disclaimer.hospital_name}
        complianceMode={disclaimer.compliance_mode}
        onAccept={handleAccept}
        onDecline={handleDecline}
      />
    );
  }

  return <>{children}</>;
};

export default DisclaimerGate;
